// client/src/components/game_hud/NeedsIndicator.jsx
// Итерация 5: индикатор одной нужды (голод / жажда).
// Дизайн A: иконка + число, без полоски.
//
// Опции:
//   icon      — React-элемент (эмодзи или MUI-иконка)
//   label     — подпись ("Голод" / "Жажда")
//   current   — текущее значение (0..max)
//   max       — максимум
//   rate      — скорость изменения в секунду (для интерполяции)
//   invert    — если true, высокое значение = плохо (голод, жажда); иначе = хорошо
//   isStale   — приглушённый вид

import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

const NeedsIndicator = ({
  icon,
  label,
  current = 0,
  max = 100,
  rate = 0,
  invert = true,
  isStale = false,
}) => {
  const safeMax = max > 0 ? max : 100;
  const safeCurrent = Math.max(0, Math.min(current, safeMax));
  const pct = (safeCurrent / safeMax) * 100;

  // Плавная интерполяция между тиками WS (по rate).
  // rate — в единицах в секунду. За 5 сек между тиками значение меняется на rate*5.
  const [displayValue, setDisplayValue] = useState(safeCurrent);
  const anchorRef = useRef({ value: safeCurrent, time: Date.now() });

  useEffect(() => {
    anchorRef.current = { value: safeCurrent, time: Date.now() };
    setDisplayValue(safeCurrent);
  }, [safeCurrent]);

  useEffect(() => {
    if (!rate || isStale) return undefined;

    const interval = setInterval(() => {
      const { value, time } = anchorRef.current;
      const elapsed = (Date.now() - time) / 1000;
      const predicted = value + rate * elapsed;
      setDisplayValue(Math.max(0, Math.min(safeMax, predicted)));
    }, 500);

    return () => clearInterval(interval);
  }, [rate, safeMax, isStale]);

  const getColor = () => {
    if (invert) {
      // Голод/жажда: 0 = хорошо, 100 = плохо
      if (pct < 30) return '#22c55e';
      if (pct < 60) return '#eab308';
      if (pct < 85) return '#f97316';
      return '#ef4444';
    }
    // Обратное (например, сытость): 100 = хорошо
    if (pct > 70) return '#22c55e';
    if (pct > 40) return '#eab308';
    return '#ef4444';
  };

  return (
    <Tooltip title={`${label}: ${Math.round(displayValue)}/${Math.round(safeMax)}`} arrow>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          opacity: isStale ? 0.5 : 1,
          transition: 'opacity 0.3s ease',
          cursor: 'help',
        }}
      >
        <Box sx={{ fontSize: '0.75rem', lineHeight: 1 }}>{icon}</Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.55rem',
            fontWeight: 700,
            color: isStale ? 'text.disabled' : getColor(),
            minWidth: 22,
            textAlign: 'right',
          }}
        >
          {Math.round(displayValue)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default NeedsIndicator;
